import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, useSpring, animate } from "framer-motion";
import { Sparkles, Users, Gift, ArrowRight, Lock, Unlock, Coins } from "lucide-react";
import { api } from "@/lib/apiClient";

/**
 * Community Vault — flagship homepage cinematic section.
 * A giant golden/steel vault door with animated spokes, orbiting sparks,
 * dynamic lighting, and a live progress ring showing community contribution.
 */
export default function CommunityVaultShowcase() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "0px 0px -20% 0px" });
  const [vault, setVault] = useState(null);

  useEffect(() => {
    api.get("/vault/current").then(({ data }) => setVault(data)).catch(() => {});
  }, []);

  if (!vault || vault.active === false) return null;
  const { campaign, percent } = vault;
  const isUnlocked = campaign?.status === "unlocked";

  return (
    <section
      ref={sectionRef}
      data-testid="home-community-vault"
      className="relative overflow-hidden py-24 lg:py-32"
      style={{
        background:
          "radial-gradient(ellipse at 20% 20%, #1e0d3e 0%, #0e0a24 40%, #0a0714 80%, #050308 100%)",
      }}
    >
      {/* ---- Deep space ambient background ---- */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <motion.div
          className="absolute -top-32 -left-24 w-[560px] h-[560px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(255,201,60,0.24), transparent 60%)" }}
          animate={{ x: [0, 30, 0], y: [0, 20, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 -right-32 w-[520px] h-[520px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(139,92,246,0.28), transparent 60%)" }}
          animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Distant stars */}
        {Array.from({ length: 60 }).map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              left: `${(i * 37) % 100}%`,
              top: `${(i * 61) % 100}%`,
              width: 2 + (i % 3),
              height: 2 + (i % 3),
              opacity: 0.15 + (i % 5) * 0.08,
              filter: "blur(0.3px)",
            }}
          />
        ))}
        {/* Faint scan grid */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,201,60,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,201,60,0.6) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          {/* LEFT — Copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-[11px] uppercase tracking-[0.32em] inline-flex items-center gap-2"
              style={{ color: "#FFC93C" }}
            >
              <Sparkles size={13} /> Community Vault — Live
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-5xl lg:text-6xl mt-3 leading-[1.02] text-brand-cream"
            >
              Unlock together.
              <br />
              <span
                style={{
                  background:
                    "linear-gradient(90deg, #FFC93C 0%, #FFE07A 40%, #F5A623 70%, #FFC93C 100%)",
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "vaultShimmer 5s ease-in-out infinite",
                }}
              >
                Reap together.
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-brand-cream/75 text-lg max-w-lg leading-relaxed"
              data-testid="vault-campaign-title"
            >
              {campaign.description}
            </motion.p>

            {/* Live progress + contributors */}
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md" data-testid="vault-counters">
              <VaultStat
                inView={inView}
                label="Progress"
                value={campaign.current_progress}
                suffix={` / ${campaign.community_goal}`}
                delay={0.3}
                icon={Coins}
              />
              <VaultStat
                inView={inView}
                label="Contributors"
                value={campaign.contributing_members_count || 0}
                delay={0.4}
                icon={Users}
              />
              <VaultStat
                inView={inView}
                label="Reward"
                value={Math.round(percent)}
                suffix="%"
                delay={0.5}
                icon={Gift}
              />
            </div>

            {/* Reward preview strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-6 rounded-2xl border border-brand-yellow/30 bg-brand-yellow/[0.08] backdrop-blur-xl p-5"
              data-testid="vault-reward-preview"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-brand-yellow flex items-center gap-2">
                <Gift size={12} /> Inside the Vault
              </div>
              <div className="mt-1.5 font-display text-2xl text-brand-cream leading-tight">
                {campaign.reward_title}
              </div>
              <div className="text-sm text-brand-cream/70 mt-1">
                {campaign.reward_description}
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link
                to="/member/vault"
                data-testid="vault-cta-contribute"
                className="relative group rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 overflow-hidden text-[#1a0f2f] transition-all duration-300"
                style={{
                  background:
                    "linear-gradient(135deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)",
                  boxShadow:
                    "0 10px 30px rgba(255,201,60,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
                }}
              >
                <Coins size={16} /> Contribute Pop Coins
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/member/vault"
                data-testid="vault-cta-open"
                className="rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2 bg-white/10 border border-white/20 text-brand-cream backdrop-blur-xl hover:bg-white/20 transition-all"
              >
                {isUnlocked ? <Unlock size={14} /> : <Lock size={14} />}
                {isUnlocked ? "Open the Vault" : "View progress"}
              </Link>
            </motion.div>
          </div>

          {/* RIGHT — Cinematic vault door */}
          <div className="relative min-h-[520px] flex items-center justify-center">
            <VaultDoor inView={inView} percent={percent} unlocked={isUnlocked} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes vaultShimmer { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes vaultPulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
      `}</style>
    </section>
  );
}

/* ------------------ VAULT DOOR ------------------ */
function VaultDoor({ inView, percent = 0, unlocked = false }) {
  const size = 460; // px
  const cx = size / 2;
  const cy = size / 2;
  const ringR = 200;
  const circumference = 2 * Math.PI * ringR;
  const dashOffset = circumference * (1 - Math.min(1, percent / 100));

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Radial rays */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "conic-gradient(from 0deg, rgba(255,201,60,0.14) 0deg, transparent 20deg, rgba(255,201,60,0.14) 40deg, transparent 60deg, rgba(255,201,60,0.14) 80deg, transparent 100deg, rgba(255,201,60,0.14) 120deg, transparent 140deg, rgba(255,201,60,0.14) 160deg, transparent 180deg, rgba(255,201,60,0.14) 200deg, transparent 220deg, rgba(255,201,60,0.14) 240deg, transparent 260deg, rgba(255,201,60,0.14) 280deg, transparent 300deg, rgba(255,201,60,0.14) 320deg, transparent 340deg)",
            maskImage:
              "radial-gradient(circle, transparent 55%, black 70%, transparent 95%)",
            WebkitMaskImage:
              "radial-gradient(circle, transparent 55%, black 70%, transparent 95%)",
          }}
        />
      </motion.div>

      {/* Progress ring (svg) */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0"
      >
        <defs>
          <linearGradient id="vaultGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFE07A" />
            <stop offset="50%" stopColor="#FFC93C" />
            <stop offset="100%" stopColor="#E5A100" />
          </linearGradient>
          <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Track */}
        <circle
          cx={cx}
          cy={cy}
          r={ringR}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="10"
        />
        {/* Filled progress */}
        <motion.circle
          cx={cx}
          cy={cy}
          r={ringR}
          fill="none"
          stroke="url(#vaultGrad)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 2.2, ease: "easeOut", delay: 0.4 }}
          filter="url(#glow)"
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>

      {/* Vault door body */}
      <motion.div
        initial={{ scale: 0.7, opacity: 0, rotate: -8 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 90, damping: 16, delay: 0.15 }}
        className="absolute rounded-full"
        style={{
          left: cx - 170,
          top: cy - 170,
          width: 340,
          height: 340,
          background:
            "radial-gradient(circle at 30% 25%, #6a5836 0%, #3a2e1a 45%, #1f180b 100%)",
          boxShadow:
            "inset 0 8px 20px rgba(255,255,255,0.12), inset 0 -8px 30px rgba(0,0,0,0.65), 0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        {/* Concentric grooves */}
        <div
          className="absolute inset-4 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 30% 25%, #7a6540 0%, #40311a 60%, #221806 100%)",
            boxShadow:
              "inset 0 4px 14px rgba(255,255,255,0.08), inset 0 -6px 22px rgba(0,0,0,0.5)",
          }}
        />
        <div
          className="absolute inset-10 rounded-full border border-yellow-800/30"
          style={{
            background:
              "radial-gradient(circle at 30% 25%, #8c7548 0%, #4b3a1f 55%, #251a09 100%)",
            boxShadow:
              "inset 0 3px 10px rgba(255,255,255,0.1), inset 0 -6px 20px rgba(0,0,0,0.55)",
          }}
        />

        {/* Rivets around the outer band */}
        {Array.from({ length: 16 }).map((_, i) => {
          const angle = (i / 16) * Math.PI * 2;
          const rivetR = 148;
          const rx = 170 + Math.cos(angle) * rivetR - 6;
          const ry = 170 + Math.sin(angle) * rivetR - 6;
          return (
            <span
              key={i}
              className="absolute rounded-full"
              style={{
                left: rx,
                top: ry,
                width: 12,
                height: 12,
                background:
                  "radial-gradient(circle at 30% 30%, #ffe17a, #b8860b 55%, #4a340a 100%)",
                boxShadow:
                  "inset 0 1px 2px rgba(255,255,255,0.6), 0 0 4px rgba(255,201,60,0.5)",
              }}
            />
          );
        })}

        {/* Rotating spoke handle */}
        <motion.div
          className="absolute"
          style={{
            left: 70,
            top: 70,
            width: 200,
            height: 200,
          }}
          animate={{ rotate: unlocked ? [0, 60, 45] : 360 }}
          transition={{
            duration: unlocked ? 2 : 24,
            repeat: unlocked ? 0 : Infinity,
            ease: unlocked ? "easeOut" : "linear",
          }}
        >
          {/* Spokes */}
          {[0, 60, 120, 180, 240, 300].map((deg, i) => (
            <div
              key={i}
              className="absolute left-1/2 top-1/2"
              style={{
                width: 96,
                height: 10,
                marginLeft: -48,
                marginTop: -5,
                transform: `rotate(${deg}deg)`,
                background:
                  "linear-gradient(90deg, #4a340a 0%, #b8860b 25%, #ffe17a 50%, #b8860b 75%, #4a340a 100%)",
                borderRadius: 6,
                boxShadow:
                  "inset 0 1px 1px rgba(255,255,255,0.35), 0 2px 3px rgba(0,0,0,0.5)",
              }}
            />
          ))}
          {/* Center hub */}
          <div
            className="absolute left-1/2 top-1/2 rounded-full flex items-center justify-center"
            style={{
              width: 72,
              height: 72,
              marginLeft: -36,
              marginTop: -36,
              background:
                "radial-gradient(circle at 30% 30%, #fff1b3 0%, #E5A100 55%, #6a4e0a 100%)",
              boxShadow:
                "inset 0 2px 4px rgba(255,255,255,0.5), 0 6px 18px rgba(255,201,60,0.6)",
            }}
          >
            <motion.div
              animate={{ scale: unlocked ? [1, 1.2, 1] : [1, 1.08, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              {unlocked ? (
                <Unlock size={28} className="text-[#3a2200]" strokeWidth={2.5} />
              ) : (
                <Lock size={28} className="text-[#3a2200]" strokeWidth={2.5} />
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Percent label at top */}
        <div
          className="absolute inset-x-0 flex justify-center"
          style={{ top: 14 }}
        >
          <div className="rounded-full bg-black/50 border border-brand-yellow/40 backdrop-blur-md px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-brand-yellow font-semibold">
            {Math.round(percent)}% filled
          </div>
        </div>
      </motion.div>

      {/* Orbiting sparkles */}
      {[0, 60, 120, 180, 240, 300].map((deg, i) => (
        <motion.div
          key={i}
          className="absolute top-1/2 left-1/2"
          animate={{ rotate: 360 }}
          transition={{ duration: 18 + i * 2, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "0 0" }}
          initial={{ rotate: deg }}
        >
          <motion.div
            className="absolute h-6 w-6 rounded-full"
            style={{
              left: 210,
              top: -12,
              background:
                "radial-gradient(circle, #FFE07A 0%, #FFC93C 40%, transparent 70%)",
              filter: "blur(0.5px)",
            }}
            animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.2, 0.8] }}
            transition={{ duration: 3 + i * 0.4, repeat: Infinity }}
          />
        </motion.div>
      ))}
    </div>
  );
}

/* ------------------ HELPERS ------------------ */
function VaultStat({ inView, label, value, suffix = "", delay, icon: Icon }) {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 60, damping: 22 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(mv, Number(value) || 0, { duration: 1.6, ease: "easeOut", delay });
    const unsub = spring.on("change", (v) => setDisplay(Math.floor(v)));
    return () => { controls.stop(); unsub(); };
    // eslint-disable-next-line
  }, [value]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-xl p-4"
    >
      <div className="text-[10px] uppercase tracking-widest inline-flex items-center gap-1 text-brand-yellow">
        <Icon size={11} /> {label}
      </div>
      <div className="mt-1 font-display text-2xl text-brand-cream leading-none">
        {display}{suffix}
      </div>
    </motion.div>
  );
}
